import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import moment from 'moment';
import { toastr } from 'react-redux-toastr';
import { useTranslation } from 'react-i18next';
import { endOfDay } from 'date-fns';
import FileSaver from 'file-saver';

import CustomButton from '../../CustomButton';
import DateDropdown from '../../dump/menuComponents/DateDropdown';
import AddSubscriptionModal from './Modals/AddSubscriptionModal';
import SelectVehicleModal from './Modals/SelectVehicleModal';
import BaseTooltip from '../BaseTooltip';

import { getDateRangeFromTabKey } from './ReportsHelpers';
import { tableExportOptions, tableNoPaginationOptions } from '../../helpers/table';
import {
    createIFTAReport,
    getIFTAReport,
    getIFTAReportCsv
} from '../../../action/FleetAdminActions';
import { IFTA_STATUSES } from '../../../utils/constants';
import { exportIcon } from '../../helpers/iconHelpers';

const IFTA_REPORT_TABS = [
    { key: 'basic', labelKey: 'tabFuelTax', type: 'basic', titleKey: 'titleFuelTaxReport' },
    { key: 'asset', labelKey: 'tabAssetView', type: 'asset', titleKey: 'titleAssetMileageByState' },
    { key: 'fleet', labelKey: 'tabFleetView', type: 'fleet', titleKey: 'titleFleetMileageByState' },
];

const DEFAULT_IFTA_FILTERS = {
    basic: { page: 1, sizePerPage: 10, sortName: 'date', sortOrder: 'asc' },
    asset: { page: 1, sizePerPage: 10, sortName: 'jurisdiction', sortOrder: 'asc' },
    fleet: { page: 1, sizePerPage: 10, sortName: 'jurisdiction', sortOrder: 'asc' },
};

const normalizeIftaResponse = (response, reportType) => {
    if (Array.isArray(response)) {
        return {
            type: reportType,
            report: response,
            total_items: response.length,
            status: IFTA_STATUSES.DONE
        };
    }

    const { report, devices, states, jurisdictions, items, ...rest } = response;
    return {
        type: reportType,
        ...rest,
        report: report || devices || states || jurisdictions || items || [],
        total_items: response.total_items || response.total_devices || response.total || 0,
    };
};

const IFTAReportTable = ({ selectedReport, goBack }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation('translation', { keyPrefix: 'reportsPage.standardReportTable' });
    const user = useSelector(state => state.user.user);
    const company = useSelector(state => state.company.company);
    const iftaReportData = useSelector(state => state.ifta.data);
    const iftaFilters = useSelector(state => state.ifta.filters);

    const userRole = user.roles[0];
    const userTZ = user.settings?.time_zone || null;
    const localTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

    let initTabKey = 3;
    switch (selectedReport.defaultDateRange) {
        case 'Today':
            initTabKey = 1;
            break;
        case 'Yesterday':
            initTabKey = 2;
            break;
        case 'Last Week':
            initTabKey = 4;
            break;
        case 'This Month':
            initTabKey = 5;
            break;
        case 'Last Month':
            initTabKey = 6;
            break;
        default:
            break;
    }

    const dateRange = getDateRangeFromTabKey(initTabKey);
    const [start_date, setStart_date] = useState(dateRange[0]);
    const [end_date, setEnd_date] = useState(dateRange[1]);
    const [reportData, setReportData] = useState([]);
    const [reportAdditionalData, setReportAdditionalData] = useState({});
    const [showAddSubscription, setShowAddSubscription] = useState(false);
    const [showSelectVehicles, setShowSelectVehicles] = useState(false);
    const [selectedVehicles, setSelectedVehicles] = useState([]);
    const [iftaActiveTabKey, setIftaActiveTabKey] = useState(IFTA_REPORT_TABS[0].key);

    const iftaPollingRef = useRef(null);
    const iftaTabInitializedRef = useRef(false);
    const iftaActiveTab = IFTA_REPORT_TABS.find(tab => tab.key === iftaActiveTabKey) || IFTA_REPORT_TABS[0];
    const activeIftaFilters = iftaFilters?.page ? iftaFilters : DEFAULT_IFTA_FILTERS[iftaActiveTab.type];
    const iftaStatus = iftaReportData.status;
    const activeIftaTotalItems = iftaReportData.type === iftaActiveTab.type ? iftaReportData.total_items : 0;
    const iftaRequiresSelectedVehicles = ['basic', 'asset'].includes(iftaActiveTab.type);
    const iftaCreationDisabled = [IFTA_STATUSES.PENDING, IFTA_STATUSES.PROCESSING].includes(iftaStatus)
        || (iftaRequiresSelectedVehicles && !selectedVehicles.length);

    const toggleLoader = useCallback((show) => {
        dispatch({ type: 'TOGGLE_LOADER', payload: show });
    }, [dispatch]);

    const nameFormat = (name) => i18n.t(`reportsPage.standardReportData.${name.replace(/\s/g, '_')}`);
    const dateFormat = (date, format = 'MM/DD/YYYY') => date ? moment(date).format(format) : '';
    const distanceFormatter = (value) => value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const totalDistanceFormatter = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        const numericValue = Number(value);
        const formattedValue = Number.isNaN(numericValue) ? value : numericValue.toFixed(2);
        return distanceFormatter(formattedValue);
    };

    const setIftaFilters = (filters) => {
        dispatch({
            type: 'SET_PAGINATION_FILTERS',
            payload: { filters }
        });
    };

    const setIftaStatus = (reportType, status) => {
        dispatch({
            type: 'SET_REPORT_STATUS',
            payload: { reportType, status }
        });
    };

    const fetchIftaReport = useCallback((reportType, filters, onStatusUpdate, options = {}) => {
        if (options.showLoader !== false) {
            toggleLoader(true);
        }
        return getIFTAReport({
            company_id: company.company_id,
            page_number: filters.page,
            page_size: filters.sizePerPage,
            order: filters.sortName,
            direction: filters.sortOrder,
            type: reportType,
            ...(reportType !== 'fleet' && {
                include_data: 'filters',
            }),
            ...(user.speed_unit === 'mph' && {
                unit: 'miles'
            }),
        }, userRole, company.name, company.partner)
            .then(res => {
                const { result, response } = res.data;
                if (result === 'OK') {
                    const data = normalizeIftaResponse(response, reportType);
                    dispatch({
                        type: 'GET_IFTA_REPORT_SUCCESS',
                        payload: {
                            reportType,
                            data
                        }
                    });
                    if (onStatusUpdate) onStatusUpdate(data.status);
                }
                if (options.showLoader !== false) {
                    toggleLoader(false);
                }
            })
            .catch(() => {
                toastr.error(t('toastrUnableGetData'));
                if (onStatusUpdate) onStatusUpdate(IFTA_STATUSES.ERROR);
                if (options.showLoader !== false) {
                    toggleLoader(false);
                }
            });
    }, [company.company_id, company.name, company.partner, dispatch, t, toggleLoader, user.speed_unit, userRole]);

    useEffect(() => {
        fetchIftaReport(iftaActiveTab.type, activeIftaFilters);

        return () => {
            if (iftaPollingRef.current) {
                clearInterval(iftaPollingRef.current);
                iftaPollingRef.current = null;
            }
        };
    }, [company.company_id]);

    useEffect(() => {
        const { status, report, ...rest } = iftaReportData;
        if (iftaReportData.type === iftaActiveTab.type && status === IFTA_STATUSES.DONE) {
            setReportData(report);
            setReportAdditionalData(rest);
        } else {
            setReportData([]);
            setReportAdditionalData({});
        }
    }, [iftaReportData, iftaActiveTab.type]);

    useEffect(() => {
        if (!iftaTabInitializedRef.current) {
            iftaTabInitializedRef.current = true;
            return;
        }

        const defaultFilters = DEFAULT_IFTA_FILTERS[iftaActiveTab.type];
        setIftaFilters(defaultFilters);
        fetchIftaReport(iftaActiveTab.type, defaultFilters);
    }, [iftaActiveTabKey]);

    const changeDate = (selected_start_date, selected_end_date) => {
        setStart_date(selected_start_date ? moment(selected_start_date).format('YYYYMMDD') : start_date);
        setEnd_date(selected_end_date ? moment(selected_end_date).format('YYYYMMDD') : end_date);
    };

    const handlePageChange = (page, sizePerPage) => {
        const newFilters = {
            ...activeIftaFilters,
            page: Number(page),
            sizePerPage: Number(sizePerPage)
        };
        setIftaFilters(newFilters);
        fetchIftaReport(iftaActiveTab.type, newFilters);
    };

    const handleSortChange = (sortName, sortOrder) => {
        const newFilters = { ...activeIftaFilters, sortName, sortOrder };
        setIftaFilters(newFilters);
        fetchIftaReport(iftaActiveTab.type, newFilters);
    };

    const createNewIftaReport = () => {
        toggleLoader(true);
        createIFTAReport(userRole, {
            company_id: company.company_id,
            partner_name: company.partner,
            company_name: company.name,
            start_date,
            end_date,
            type: iftaActiveTab.type === 'fleet' ? 'fleet' : 'asset',
            ...(iftaActiveTab.type !== 'fleet' && {
                device_ids: selectedVehicles.join(',')
            }),
            tzname: userTZ ? userTZ : localTZ
        })
            .then(res => {
                const { result, response } = res.data;
                if (result === 'OK') {
                    const { status } = response;
                    setIftaStatus(iftaActiveTab.type, IFTA_STATUSES[status]);
                    toastr.success(t('toastrRequestReportSuccess'));
                    if ([IFTA_STATUSES.PENDING, IFTA_STATUSES.PROCESSING].includes(status)) {
                        iftaPollingRef.current = setInterval(() => {
                            fetchIftaReport(iftaActiveTab.type, activeIftaFilters, updatedStatus => {
                                if (updatedStatus === IFTA_STATUSES.DONE || updatedStatus === IFTA_STATUSES.ERROR) {
                                    clearInterval(iftaPollingRef.current);
                                    iftaPollingRef.current = null;
                                }
                            });
                        }, 5000);
                    } else if (status === IFTA_STATUSES.DONE) {
                        fetchIftaReport(iftaActiveTab.type, activeIftaFilters);
                    }
                }
                setSelectedVehicles([]);
                toggleLoader(false);
            })
            .catch(() => {
                toastr.error(t('toastrUnableCreateReport'));
                toggleLoader(false);
                setSelectedVehicles([]);
            });
    };

    const onExportClick = () => {
        getIFTAReportCsv({
            start_date,
            end_date,
            company_id: company.company_id,
            order: activeIftaFilters.sortName,
            direction: activeIftaFilters.sortOrder,
            type: iftaActiveTab.type,
            ...(user.speed_unit === 'mph' && {
                unit: 'miles'
            }),
        }, userRole, company.name, company.partner).then(res => {
            const blob = new Blob([res.data], { type: 'text/csv' });
            const reportName = iftaActiveTab.titleKey ? t(iftaActiveTab.titleKey) : selectedReport.name;
            const name = reportName.toLowerCase().replaceAll(' ', '_');
            const timeframe = moment(start_date).format('M-D-YY') + (start_date === end_date ? '' : '--' + moment(end_date).format('M-D-YY'));
            FileSaver.saveAs(blob, name + t('CSVFileName') + timeframe + '.csv');
        }).catch(() => {
            toastr.error(t('toastrUnableExportCsv'));
        });
    };

    const getReportTooltip = () => {
        if ([IFTA_STATUSES.PENDING, IFTA_STATUSES.PROCESSING].includes(iftaStatus)) {
            return t('reportInprogressTooltip');
        }
        if (iftaRequiresSelectedVehicles && !selectedVehicles.length) {
            return t('runReportTooltip');
        }
        return null;
    };

    const goBackFunc = () => {
        if (window.history.state.prevPath !== '/zones') {
            goBack();
        } else {
            navigate(-1);
        }
    };

    const getReportRows = (data) => {
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.report)) return data.report;
        if (Array.isArray(data?.devices)) return data.devices;
        if (Array.isArray(data?.states)) return data.states;
        if (Array.isArray(data?.jurisdictions)) return data.jurisdictions;
        if (Array.isArray(data?.items)) return data.items;
        return [];
    };

    const formatMileageDistance = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        const unit = user.speed_unit === 'kph' ? 'Km' : 'Mi';
        return `${distanceFormatter(value)} ${unit}`;
    };

    const normalizeMileageRows = (rows) => getReportRows(rows).map((row, index) => ({
        ...row,
        _rowKey: `${row.id || row.jurisdiction || row.state || row.name || 'row'}-${index}`,
        jurisdiction: row.jurisdiction || row.state || row.name || '-',
        distance: row.distance ?? row.total_distance ?? row.mileage ?? '-',
    }));

    const normalizeAssetReports = (rows) => getReportRows(rows).map((asset, index) => ({
        ...asset,
        _assetKey: `${asset.device_id || asset.vehicle_name || asset.asset || 'asset'}-${index}`,
        assetName: asset.vehicle_name || asset.asset_name || asset.asset || asset.device_name || asset.device_id || '-',
        rows: normalizeMileageRows(asset),
    }));

    const mileageColumns = [
        { name: 'Jurisdiction', dataField: 'jurisdiction', width: '60%', dataSort: true },
        { name: 'Distance', dataField: 'distance', width: '40%', dataSort: true },
    ];

    const renderMileageTable = (rows, options = {}) => (
        <BootstrapTable
            data={normalizeMileageRows(rows)}
            {...tableNoPaginationOptions}
            exportCSV={false}
            options={{
                ...options,
            }}
            containerClass={selectedReport.containerClass}
        >
            <TableHeaderColumn isKey hidden dataField="_rowKey" />
            {mileageColumns.map(column => (
                <TableHeaderColumn
                    key={column.dataField}
                    width={column.width}
                    dataField={column.dataField}
                    dataFormat={column.dataField === 'distance' ? formatMileageDistance : (cell) => cell}
                    dataSort={column.dataSort}
                >
                    {nameFormat(column.name)}
                </TableHeaderColumn>
            ))}
        </BootstrapTable>
    );

    const tableReportData = Array.isArray(reportData) ? reportData : getReportRows(reportData);

    return (
        <div className="report">
            <div className="ifta-report-tabs">
                {IFTA_REPORT_TABS.map(tab => (
                    <button
                        key={tab.key}
                        type="button"
                        className={`ifta-report-tab${iftaActiveTabKey === tab.key ? ' active' : ''}`}
                        onClick={() => setIftaActiveTabKey(tab.key)}
                    >
                        {t(tab.labelKey)}
                    </button>
                ))}
            </div>
            <div className="page-subheader">
                <div className="subheader-section search-flex report-search ifta-section">
                    <DateDropdown
                        start_date={start_date}
                        end_date={end_date}
                        onSelectDate={changeDate}
                        maxDate={endOfDay(new Date())}
                    />
                    {iftaRequiresSelectedVehicles ? (
                        <CustomButton
                            variant="primary-outline"
                            size="sm"
                            onClick={() => setShowSelectVehicles(true)}
                        >
                            {t('selectVehicles')}
                        </CustomButton>
                    ) : ''}
                    <BaseTooltip
                        body={getReportTooltip()}
                        {...(!iftaCreationDisabled && { show: false })}
                    >
                        <div>
                            <CustomButton
                                variant="primary"
                                size="sm"
                                disabled={iftaCreationDisabled}
                                style={{ marginLeft: '10px' }}
                                onClick={createNewIftaReport}
                            >
                                {t('runNewReport')}
                            </CustomButton>
                        </div>
                    </BaseTooltip>
                </div>
                <div className="subheader-section">
                    <CustomButton variant="primary-outline" size="sm" onClick={() => setShowAddSubscription(true)}>
                        {t('btnAutomateReport')}
                    </CustomButton>
                    <CustomButton variant="secondary-outline" onClick={goBackFunc}>{t('btnBack')}</CustomButton>
                </div>
            </div>

            <main className="main-content-block">
                {/* Container with report title and total data  */}
                <div className="table-header">
                    <span className="report-title">{t(iftaActiveTab.titleKey)}</span>
                    <div className="ifta-info-container">
                        <div className="ifta-info-column">
                            <p>{t('reportDateRange')}:</p>
                            <p>{t('totalDistance')}:</p>
                        </div>
                        <div className="ifta-info-column">
                            <p>{dateFormat(reportAdditionalData?.start_date)} - {dateFormat(reportAdditionalData?.end_date)}</p>
                            <p>
                                {reportAdditionalData?.total_distance !== null && reportAdditionalData?.total_distance !== undefined ? (
                                    <>
                                        <span>{totalDistanceFormatter(reportAdditionalData.total_distance)}</span>&nbsp;
                                        <span>{user.speed_unit === 'kph' ? 'Km' : 'Mi'}</span>
                                    </>
                                ) : '-'}
                            </p>
                        </div>
                    </div>
                </div>
                {/* Container with Export btn */}
                <div className="export-btn-container">
                    <div className="btn-group">
                        <CustomButton
                            onClick={onExportClick}
                            variant="primary-outline"
                            size="sm"
                            prefix="export-button"
                        >
                            {exportIcon}
                            <span className="export-label">{t('titleExport')}</span>
                        </CustomButton>
                    </div>
                </div>
                {/* Container with report's table */}
                {
                    iftaActiveTab.type === 'asset' 
                    ? (
                        <div className="ifta-asset-report">
                            {normalizeAssetReports(tableReportData).map(asset => (
                                <div className="ifta-asset-section" key={asset._assetKey}>
                                    <div className="ifta-asset-title">{asset.assetName}</div>
                                    {renderMileageTable(asset.rows)}
                                </div>
                            ))}
                            <BootstrapTable
                                data={[]}
                                remote
                                fetchInfo={{ dataTotalSize: activeIftaTotalItems }}
                                options={{
                                    ...tableExportOptions.options,
                                    onPageChange: handlePageChange,
                                    page: activeIftaFilters.page,
                                    sizePerPage: activeIftaFilters.sizePerPage,
                                }}
                                pagination
                                exportCSV={false}
                                containerClass="ifta-device-pager"
                            >
                                <TableHeaderColumn isKey hidden dataField="id" />
                            </BootstrapTable>
                        </div>
                    ) 
                    : iftaActiveTab.type === 'fleet' 
                        ? (
                            renderMileageTable(tableReportData, {
                                onSortChange: handleSortChange,
                                sortName: activeIftaFilters.sortName,
                                sortOrder: activeIftaFilters.sortOrder,
                            })
                        ) 
                        : (
                            <BootstrapTable
                                data={tableReportData}
                                {...tableExportOptions}
                                remote
                                fetchInfo={{ dataTotalSize: activeIftaTotalItems }}
                                exportCSV={false}
                                options={{
                                    ...tableExportOptions.options,
                                    onPageChange: handlePageChange,
                                    page: activeIftaFilters.page,
                                    sizePerPage: activeIftaFilters.sizePerPage,
                                    sortName: activeIftaFilters.sortName,
                                    sortOrder: activeIftaFilters.sortOrder,
                                    onSortChange: handleSortChange,
                                }}
                                containerClass={selectedReport.containerClass}
                            >
                                {selectedReport.columns.map(column => (
                                    <TableHeaderColumn
                                        key={column.dataField}
                                        width={column.width}
                                        isKey={column.isKey || false}
                                        hidden={column.hidden || false}
                                        dataField={column.dataField}
                                        dataFormat={column.dataFormat ? (cell, row) => column.dataFormat(cell, row) : (cell) => cell}
                                        dataSort={column.dataSort}
                                        csvFormat={column.csvFormat ? (cell, row) => column.csvFormat(cell, row) : (cell) => cell}
                                        csvHeader={column.csvHeader ? column.csvHeader : nameFormat(column.name)}
                                        tdStyle={column.tdStyle ? column.tdStyle : {}}
                                        export={column.export}
                                    >
                                        {!column.hidden && nameFormat(column.name)}
                                    </TableHeaderColumn>
                                ))}
                            </BootstrapTable>
                        )
                }
            </main>
            {showAddSubscription && (
                <AddSubscriptionModal
                    onHide={() => setShowAddSubscription(false)}
                    report={{
                        type: 'standard',
                        report_id: selectedReport.id,
                        additional: iftaActiveTab.type
                    }}
                />
            )}
            {showSelectVehicles ? (
                <SelectVehicleModal
                    show={showSelectVehicles}
                    selectedVehicles={selectedVehicles}
                    setSelectedVehicles={setSelectedVehicles}
                    onHide={() => setShowSelectVehicles(false)}
                />
            ) : ''}
        </div>
    );
};

export default IFTAReportTable;
